import { useMemo } from 'react';
import { Select, Option, Input, CircularProgress, Box } from '@mui/joy';
import { usePubliciteFromMandat } from '@app/hooks/usePubliciteFromMandat';

interface PubliciteSelectProps {
    value: string;
    onChange: (value: string) => void;
    mandatId: number | null | undefined;
    disabled?: boolean;
    size?: 'sm' | 'md' | 'lg';
}

export const PubliciteSelect = ({
    value,
    onChange,
    mandatId,
    disabled = false,
    size = 'sm',
}: PubliciteSelectProps) => {
    const { publiciteOptions, loading } = usePubliciteFromMandat(mandatId);

    // Merge current value with options if it doesn't exist in the list
    const optionsWithCurrentValue = useMemo(() => {
        if (!value || loading) return publiciteOptions;

        // Check if current value exists in options
        const valueExists = publiciteOptions.some(
            (opt) => opt.trim().toLowerCase() === value.trim().toLowerCase()
        );

        // If value doesn't exist, add it at the beginning
        if (!valueExists && value.trim()) {
            return [value.trim(), ...publiciteOptions];
        }

        return publiciteOptions;
    }, [publiciteOptions, value, loading]);

    // If no options available, show text input
    if (!loading && publiciteOptions.length === 0) {
        return (
            <Input
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                size={size}
                placeholder="Entrez la publicité"
            />
        );
    }

    // Show loading state
    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size="sm" />
                <Input
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={true}
                    size={size}
                    placeholder="Chargement..."
                />
            </Box>
        );
    }

    // Show select with options (including current value if not in list)
    return (
        <Select
            value={value || ''}
            onChange={(_, newValue) => onChange(newValue as string)}
            disabled={disabled}
            size={size}
        >
            {optionsWithCurrentValue.map((option, index) => (
                <Option
                    key={index}
                    value={option}
                    sx={
                        value &&
                        option.trim().toLowerCase() ===
                            value.trim().toLowerCase() &&
                        !publiciteOptions.some(
                            (opt) =>
                                opt.trim().toLowerCase() ===
                                value.trim().toLowerCase()
                        )
                            ? { fontStyle: 'italic', opacity: 0.7 }
                            : {}
                    }
                >
                    {option}
                    {value &&
                        option.trim().toLowerCase() ===
                            value.trim().toLowerCase() &&
                        !publiciteOptions.some(
                            (opt) =>
                                opt.trim().toLowerCase() ===
                                value.trim().toLowerCase()
                        ) &&
                        ' (ancienne valeur)'}
                </Option>
            ))}
        </Select>
    );
};

export default PubliciteSelect;
